import { useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Loader, useCurrentUser } from '@beholos/holoscommon';
import { InfoIcon, MailCheck, MailQuestion, Users } from 'lucide-react';
import { FamilyInviteForm } from './FamilyInviteForm';
import { getFamilyMembers } from '../../services/family.service';
import type { FamilyListResponse, FamilyMember } from '../../services/family.service';

const DEFAULT_MAX_MEMBERS = 3;

export default function FamilyHub() {
  const { user, loading: userLoading } = useCurrentUser();
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [maxAllowed, setMaxAllowed] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isNotPrimary, setIsNotPrimary] = useState(false);

  const acceptedCount = useMemo(
    () => members.filter((member) => member.status === 'accepted').length,
    [members],
  );

  const maxMembers = maxAllowed ?? DEFAULT_MAX_MEMBERS;
  const limitReached = acceptedCount >= maxMembers;

  const fetchFamilyMembers = async () => {
    setLoading(true);
    setError(null);
    setIsNotPrimary(false);
    try {
      const data: FamilyListResponse = await getFamilyMembers();
      setMembers(data.familyMembers);
      if (typeof data.maxAllowed === 'number') {
        setMaxAllowed(data.maxAllowed);
      }
    } catch (err: any) {
      setIsNotPrimary(true);
      // if (err?.status === 403 || err?.statusCode === 403 || err?.response?.status === 403) {
      // } else {
      //   // setError(err?.message ?? 'No se pudo cargar la información de la familia.');
      // }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFamilyMembers();
  }, []);

  const renderStatusPill = (status: FamilyMember['status']) => {
    const base = 'inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold';
    if (status === 'accepted') {
      return (
        <span className={`${base} bg-green-50 text-green-800 border border-green-200`}>
          <MailCheck size={14} /> Aceptado
        </span>
      );
    }
    return (
      <span className={`${base} bg-amber-50 text-amber-800 border border-amber-200`}>
        <MailQuestion size={14} /> Pendiente
      </span>
    );
  };

  const renderMemberCard = (member: FamilyMember) => {
    const acceptedAt = member.acceptedAt || member.invitedAt;
    const formattedDate = acceptedAt
      ? format(typeof acceptedAt === 'string' ? parseISO(acceptedAt) : acceptedAt, 'dd MMM yyyy', {
        locale: es,
      })
      : '—';

    return (
      <div
        key={member.id}
        className="flex flex-col gap-2 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm text-gray-600">Nombre</p>
            <p className="text-lg font-semibold text-gray-900">{member.firstname || 'Invitación'}</p>
          </div>
          {renderStatusPill(member.status)}
        </div>
        <div className="grid grid-cols-1 gap-3 text-sm text-gray-700 md:grid-cols-2">
          <div>
            <p className="text-gray-500">Correo</p>
            <p className="font-medium text-gray-900">{member.email}</p>
          </div>
          <div>
            <p className="text-gray-500">Última actualización</p>
            <p className="font-medium text-gray-900">{formattedDate}</p>
          </div>
        </div>
      </div>
    );
  };

  const loadingView = (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Loader />
    </div>
  );

  const nonCorporateView = (
    <div className="mx-auto max-w-5xl rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
      <h2 className="mb-3 text-2xl font-semibold text-gray-900">Plan familiar</h2>
      <p className="text-sm text-gray-700">
        El plan familiar está disponible únicamente para usuarios corporativos. Si crees que esto es un error,
        ponte en contacto con tu administrador o con el equipo de Holos.
      </p>
    </div>
  );

  const notPrimaryView = (
    <div className="mx-auto max-w-5xl rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
      <h2 className="mb-3 text-2xl font-semibold text-gray-900">Plan familiar</h2>
      <p className="text-sm text-gray-700 mb-4">
        Solo el titular de la cuenta principal puede gestionar a los miembros de la familia.
      </p>
      <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        <InfoIcon size={18} className="mt-0.5 flex-shrink-0" />
        <div>
          Si necesitas agregar o modificar miembros familiares, contacta al titular principal de tu cuenta o
          comunícate con el equipo de Holos.
        </div>
      </div>
    </div>
  );

  const panelView = (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
          <Users size={18} /> Plan familiar
        </div>
        <h2 className="text-3xl font-semibold text-gray-900">Tu Plan Familiar</h2>
        <p className="text-sm text-gray-600">
          Invita a tus familiares y revisa el estado de cada invitación. 
          Al unirse, ellos reciben su propia cuenta con acceso digital gratuito y la opción de comprar planes de alto valor a precio preferencial.
        </p>
      </div>

      {limitReached && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <InfoIcon size={18} className="mt-0.5" />
          <div>
            ¡Tu Círculo Familiar está completo! Si necesitas dar de alta a un nuevo familiar, puedes liberar un lugar eliminando a un miembro actual.
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <InfoIcon size={18} className="mt-0.5" />
          <div>{error}</div>
        </div>
      )}

      <FamilyInviteForm
        onInviteSent={fetchFamilyMembers}
        disabled={limitReached}
        maxAllowed={maxMembers}
        currentCount={acceptedCount}
      />

      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">Miembros</p>
            <h3 className="text-2xl font-semibold text-gray-900">
              {acceptedCount} / {maxMembers} ocupados
            </h3>
            <p className="text-sm text-gray-600">Pendientes no consumen cupo.</p>
          </div>
          <button
            onClick={fetchFamilyMembers}
            className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-800 transition hover:bg-gray-100"
          >
            Recargar
          </button>
        </div>

        {members.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-600">
            Aún no has agregado familiares. Envía tu primera invitación para comenzar.
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {members.map(renderMemberCard)}
          </div>
        )}
      </div>
    </div>
  );

  if (userLoading || loading) {
    return (
      <div className="bg-gray-100 py-4 px-0 md:px-2">
        {loadingView}
      </div>
    );
  }

  if (user && !user.isCorporate) {
    return nonCorporateView;
  }

  if (isNotPrimary) {
    return notPrimaryView;
  }

  return panelView
}
